import { useState, useEffect, useRef, useCallback } from 'react';
import { COLOR_OPTIONS, ANIMAL_OPTIONS } from '../constants';
import { playAudioFeedback, playAnimalSound, preloadAnimalSound } from '../audio';
import { tapHaptic } from '../native';

// The two independent orbs: each can be hidden (just-tapped) and, in
// multicolor mode, carries its own current colour. Kids mode swaps colours for
// animals; in "random" mode each orb reshuffles on every reappear.
//
// Owns the tap handler: haptic + panned sound, hide, then reappear after
// `reappearSpeed`. Orb 1 = left, orb 2 = right.
//
// - `onTap(orbNumber)`: caller-side bookkeeping (stats recording, demo steps).
// - `onOrbReappeared(orbNumber)`: the orb is back — start its reaction clock.
const useOrbs = ({ settings, onTap, onOrbReappeared, reappearSpeedRef }) => {
  const [orb1Visible, setOrb1Visible] = useState(true);
  const [orb2Visible, setOrb2Visible] = useState(true);
  const [orb1Color, setOrb1Color] = useState('blue');
  const [orb2Color, setOrb2Color] = useState('purple');
  const [orb1Animal, setOrb1Animal] = useState('cat');
  const [orb2Animal, setOrb2Animal] = useState('cat');
  const timersRef = useRef(new Set()); // pending reappear timeouts, cleared on exit/unmount
  useEffect(() => () => {
    timersRef.current.forEach(clearTimeout);
    timersRef.current.clear();
  }, []);

  // Pick a random real animal (never "random" itself, and never the current one).
  const getRandomAnimal = useCallback((current) => {
    const animals = Object.keys(ANIMAL_OPTIONS).filter(
      (a) => a !== 'random' && a !== current
    );
    return animals[Math.floor(Math.random() * animals.length)];
  }, []);

  // Keep each orb's animal in step with the chosen one. In "random" mode each orb
  // starts on its own random animal and then reshuffles on every reappear.
  useEffect(() => {
    if (settings.selectedAnimal === 'random') {
      setOrb1Animal(getRandomAnimal());
      setOrb2Animal(getRandomAnimal());
    } else {
      setOrb1Animal(settings.selectedAnimal);
      setOrb2Animal(settings.selectedAnimal);
    }
  }, [settings.selectedAnimal, getRandomAnimal]);

  // Warm the recordings in kids mode so the first tap plays the real sound rather
  // than the synth fallback. In random mode any animal can appear, so warm them all.
  useEffect(() => {
    if (!settings.kidsMode) return;
    if (settings.selectedAnimal === 'random') {
      Object.keys(ANIMAL_OPTIONS).forEach((a) => a !== 'random' && preloadAnimalSound(a));
    } else {
      preloadAnimalSound(settings.selectedAnimal);
    }
  }, [settings.kidsMode, settings.selectedAnimal]);

  // Keep the orbs' colours in step with the chosen colour. In multicolor mode
  // each orb starts on its own random colour and then cycles on every reappear.
  const getRandomColor = useCallback((current) => {
    const colors = Object.keys(COLOR_OPTIONS).filter(
      (c) => c !== 'multicolor' && c !== current
    );
    return colors[Math.floor(Math.random() * colors.length)];
  }, []);

  useEffect(() => {
    if (settings.orbColor === 'multicolor') {
      setOrb1Color(getRandomColor());
      setOrb2Color(getRandomColor());
    } else if (settings.orbColor === 'blue') {
      // The default is TapTap's branded pair, matching the sign-in hero.
      setOrb1Color('blue');
      setOrb2Color('purple');
    } else {
      setOrb1Color(settings.orbColor);
      setOrb2Color(settings.orbColor);
    }
  }, [settings.orbColor, getRandomColor]);

  // Handle an orb tap: sound (panned to that side) + haptic, then hide the orb
  // and bring it back after `reappearSpeed`.
  const handleOrbTap = useCallback((orbNumber, { automatic = false } = {}) => {
    if (!automatic && settings.hapticsEnabled !== false) tapHaptic(); // native haptic, with web Vibration API fallback

    // Caller-side bookkeeping: stats recording + demo step advancement.
    if (!automatic) onTap(orbNumber);

    const side = orbNumber === 1 ? -1 : 1;
    if (settings.kidsMode) {
      // Play the sound of the animal currently on this orb.
      const animal = orbNumber === 1 ? orb1Animal : orb2Animal;
      playAnimalSound(animal, settings.audioEnabled, settings.audioVolume, side);
    } else {
      playAudioFeedback(settings.audioEnabled, settings.audioVolume, side);
    }

    const isMulti = settings.orbColor === 'multicolor';
    const isRandomAnimal = settings.kidsMode && settings.selectedAnimal === 'random';
    const liveSpeed = Number(reappearSpeedRef?.current ?? settings.reappearSpeed ?? 0.5);
    const delay = (Number.isFinite(liveSpeed) && liveSpeed >= 0 ? liveSpeed : 0.5) * 1000;
    const setVisible = orbNumber === 1 ? setOrb1Visible : setOrb2Visible;
    const setColor = orbNumber === 1 ? setOrb1Color : setOrb2Color;
    const setAnimal = orbNumber === 1 ? setOrb1Animal : setOrb2Animal;

    setVisible(false);
    const id = setTimeout(() => {
      timersRef.current.delete(id);
      if (isMulti) setColor((c) => getRandomColor(c));
      if (isRandomAnimal) setAnimal((a) => getRandomAnimal(a));
      setVisible(true);
      onOrbReappeared(orbNumber); // start the next reaction clock
    }, delay);
    timersRef.current.add(id);
  }, [settings, orb1Animal, orb2Animal, getRandomColor, getRandomAnimal, onTap, onOrbReappeared, reappearSpeedRef]);

  // Back to the pristine state (e.g. on logout): both orbs visible.
  const resetOrbs = useCallback(() => {
    timersRef.current.forEach(clearTimeout);
    timersRef.current.clear();
    setOrb1Visible(true);
    setOrb2Visible(true);
  }, []);

  return {
    orb1Visible, orb2Visible, orb1Color, orb2Color, orb1Animal, orb2Animal,
    handleOrbTap, resetOrbs,
  };
};

export default useOrbs;
